<template>
  <div>
    <!-- 面包屑导航 -->
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-page-header content="支付流水上传"></el-page-header>
    </el-breadcrumb>
    <el-card class="box-card" style="margin-top:20px">
      <div class="container">
        <div class="header">
          <h1>CSV文件编码转换工具</h1>
          <p>解决CSV文件乱码问题，支持多种编码格式转换</p>
        </div>

        <div class="main-content">
          <!-- 上传区域 -->
          <div class="upload-section">
            <div class="section-title">
              <i class="el-icon-upload"></i>
              文件上传
            </div>

            <div class="file-type-tabs">
              <div class="file-type-tab active">CSV文件</div>
            </div>

            <el-upload
                    class="upload-demo"
                    drag
                    action="#"
                    :auto-upload="false"
                    :on-change="handleFileChange"
                    :accept="'.csv'"
                    :limit="1"
            >
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">将CSV文件拖到此处，或<em>点击上传</em></div>
              <div class="el-upload__tip" slot="tip">支持.csv格式文件</div>
            </el-upload>

            <div class="upload-tips">
              <p>上传说明：</p>
              <p>1. 支持各种编码格式的CSV文件</p>
              <p>2. 系统会自动检测文件编码</p>
              <p>3. 可将文件转换为UTF-8编码</p>
              <p>4. 支持预览转换前后的数据</p>
            </div>

            <div class="encoding-tips">
              <h4>💡 编码问题说明</h4>
              <p>CSV文件乱码通常是因为编码不匹配：</p>
              <p>• 中文Windows系统默认使用GBK/GB2312编码</p>
              <p>• 现代Web应用通常使用UTF-8编码</p>
              <p>• 本工具可将任何编码的CSV转换为UTF-8</p>
            </div>

            <div v-if="file" class="file-info">
              <p>已选择文件: {{ file.name }}</p>
              <p>文件大小: {{ formatFileSize(file.size) }}</p>
              <p>检测到的编码: {{ detectedEncoding || '正在检测...' }}</p>
              <p>检测到的字段: {{ tableHeader.length }} 个</p>
            </div>

            <div class="action-buttons">
              <el-button type="primary" icon="el-icon-search" @click="detectEncoding" :disabled="!file">
                检测编码
              </el-button>
              <el-button icon="el-icon-delete" @click="clearData">
                清空数据
              </el-button>
            </div>
          </div>

          <!-- 预览区域 -->
          <div class="preview-section">
            <div class="section-title">
              <i class="el-icon-document"></i>
              数据预览
            </div>

            <div v-if="tableData.length === 0" class="no-data">
              <i class="el-icon-document"></i>
              <p>暂无数据，请上传CSV文件</p>
            </div>

            <div v-else>
              <el-table
                      :data="tableData.slice(0, 5)"
                      border
                      stripe
                      class="preview-table"
                      max-height="300"
              >
                <el-table-column
                        v-for="(header, index) in tableHeader"
                        :key="index"
                        :prop="header"
                        :label="header"
                        min-width="120"
                >
                </el-table-column>
              </el-table>

              <div style="margin-top: 15px; text-align: center; color: #909399;" v-if="tableData.length > 5">
                仅显示前5条记录，共 {{ tableData.length }} 条记录
              </div>

              <div class="action-buttons">
                <el-button type="primary" icon="el-icon-download" @click="exportOriginalData">
                  导出原始数据
                </el-button>
              </div>
            </div>
          </div>

          <!-- 转换区域 -->
          <div class="conversion-section">
            <div class="section-title">
              <i class="el-icon-refresh"></i>
              编码转换
            </div>

            <div class="encoding-options">
              <h3>编码设置</h3>
              <el-radio-group v-model="targetEncoding" size="medium">
                <el-radio-button label="UTF-8">UTF-8</el-radio-button>
                <el-radio-button label="GBK">GBK</el-radio-button>
                <el-radio-button label="GB2312">GB2312</el-radio-button>
              </el-radio-group>
              <p style="margin-top: 10px; color: #909399; font-size: 14px;">
                选择目标编码格式（推荐使用UTF-8）
              </p>
            </div>

            <div class="conversion-options">
              <el-button
                      type="success"
                      icon="el-icon-refresh"
                      @click="convertEncoding"
                      :disabled="!file || !detectedEncoding"
                      :loading="converting"
              >
                {{ converting ? '转换中...' : '转换为 ' + targetEncoding }}
              </el-button>
            </div>

            <div v-if="convertedData" class="conversion-result">
              <h3>转换成功！</h3>
              <p>文件已成功转换为 {{ targetEncoding }} 编码</p>
              <p>转换后数据行数: {{ convertedData.length }}</p>

              <div class="action-buttons">
                <el-button type="primary" icon="el-icon-download" @click="downloadConvertedFile">
                  下载转换后的文件
                </el-button>
                <el-button type="info" icon="el-icon-view" @click="previewConvertedData">
                  预览转换数据
                </el-button>
              </div>
            </div>

            <div class="log-area">
              <div v-for="(log, index) in conversionLogs" :key="index" class="log-entry" :class="{'log-error': log.type === 'error', 'log-warning': log.type === 'warning'}">
                [{{ log.time }}] {{ log.message }}
              </div>
              <div v-if="conversionLogs.length === 0" style="color: #909399; text-align: center; padding: 20px;">
                暂无转换日志
              </div>
            </div>
          </div>
        </div>

        <div class="step-by-step">
          <h4>📋 手动转换步骤（如果自动转换失败）</h4>
          <div class="step">
            <div class="step-number">1</div>
            <div>用Excel打开CSV文件</div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div>点击"文件" → "另存为"</div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div>在保存类型中选择"CSV UTF-8 (逗号分隔) (*.csv)"</div>
          </div>
          <div class="step">
            <div class="step-number">4</div>
            <div>保存文件并重新上传到系统</div>
          </div>
        </div>

        <div class="footer">
          <p>© 2023 CSV文件编码转换工具 | 版本: v1.0.0</p>
        </div>
      </div>
    </el-card>

  </div>
</template>
<script>
  // 模拟axios的paySave方法
  // const mockAxios = {
  //   paySave: function(data) {
  //     return new Promise((resolve, reject) => {
  //       // 模拟网络延迟
  //       const delay = Math.random() * 1000 + 500;
  //
  //       setTimeout(() => {
  //         // 模拟90%的成功率
  //         if (Math.random() > 0.1) {
  //           resolve({
  //             status: 'success',
  //             message: '数据保存成功',
  //             data: {
  //               processed: data.length,
  //               success: data.length - Math.floor(Math.random() * 3),
  //               failed: Math.floor(Math.random() * 3)
  //             },
  //             timestamp: new Date().toLocaleString()
  //           });
  //         } else {
  //           reject({
  //             status: 'error',
  //             message: '保存失败：服务器内部错误',
  //             timestamp: new Date().toLocaleString()
  //           });
  //         }
  //       }, delay);
  //     });
  //   }
  // };

  import Papa from 'papaparse';
  import * as XLSX from 'xlsx';
  export default {
    name:'changeCsv',
    components: { },
    data() {
      return {
        activeTab: 'csv',
        file: null,
        tableHeader: [],
        tableData: [],
        detectedEncoding: '',
        targetEncoding: 'UTF-8',
        converting: false,
        convertedData: null,
        conversionLogs: []
      }
    },
    mounted() {
      // 初始化API日志

    },
    created() {

    },
    methods: {
      // 处理文件变化
      handleFileChange(file) {
        this.file = file.raw;
        this.tableData = [];
        this.detectedEncoding = '';
        this.convertedData = null;
        this.addLog('文件已选择: ' + this.file.name, 'info');

        // 自动检测编码
        this.detectEncoding();
      },

      // 检测文件编码
      detectEncoding() {
        if (!this.file) {
          this.$message.error('请先选择文件');
          return;
        }

        this.addLog('开始检测文件编码...', 'info');

        const fileReader = new FileReader();
        fileReader.onload = (e) => {
          try {
            const text = e.target.result;
            const encoding = this.guessEncoding(text);
            this.detectedEncoding = encoding;

            this.addLog(`检测到文件编码: ${encoding}`, 'info');

            // 使用检测到的编码解析文件
            this.parseFileWithEncoding(encoding);
          } catch (err) {
            console.error(err);
            this.$message.error('编码检测失败：' + err.message);
            this.addLog('编码检测失败: ' + err.message, 'error');
          }
        };

        // 先用UTF-8读取尝试
        fileReader.readAsText(this.file, 'UTF-8');
      },

      // 猜测文件编码 - 修复正则表达式中的控制字符
      guessEncoding(text) {
        // 检查UTF-8 BOM
        if (text.startsWith('\uFEFF')) {
          return 'UTF-8 BOM';
        }

        // 检查是否有UTF-8解码失败的替换字符
        if (text.includes('�')) {
          return 'GBK';
        }

        // 检查常见的中文乱码模式
        const garbledPatterns = ['è´§', 'å', 'æ', 'ï', 'ð', '¡¯'];
        for (let pattern of garbledPatterns) {
          if (text.includes(pattern)) {
            return 'GBK';
          }
        }

        // 检查中文字符 - 修复正则表达式，移除控制字符
        const chineseChars = text.match(/[\u4e00-\u9fa5]/g);
        // 使用更安全的非ASCII字符匹配
        // eslint-disable-next-line no-control-regex
        const nonAsciiChars = text.match(/[^\u0000-\u007F]/g);

        // 如果有非ASCII字符但没有正常显示的中文，可能是编码问题
        if (nonAsciiChars && !chineseChars) {
          return 'GBK';
        }

        return 'UTF-8';
      },

      // 使用指定编码解析文件
      parseFileWithEncoding(encoding) {
        const fileReader = new FileReader();

        fileReader.onload = (e) => {
          try {
            let text = e.target.result;

            // 如果检测到GBK但UTF-8读取失败，尝试用其他方法
            if (encoding === 'GBK' && text.includes('�')) {
              this.addLog('UTF-8解码失败，尝试二进制方式读取...', 'warning');
              this.parseAsBinary();
              return;
            }

            // 使用PapaParse解析CSV
            const csvData = Papa.parse(text, {
              header: true,
              skipEmptyLines: true,
              encoding: encoding === 'UTF-8 BOM' ? 'UTF-8' : encoding
            });

            this.processCSVData(csvData);
          } catch (err) {
            console.error(err);
            this.$message.error('文件解析失败：' + err.message);
            this.addLog('文件解析失败: ' + err.message, 'error');
          }
        };

        if (encoding === 'GBK') {
          // 尝试用二进制方式读取
          this.parseAsBinary();
        } else {
          fileReader.readAsText(this.file, 'UTF-8');
        }
      },

      // 以二进制方式解析文件
      parseAsBinary() {
        const fileReader = new FileReader();

        fileReader.onload = (e) => {
          try {
            const arrayBuffer = e.target.result;

            // 尝试多种编码
            const encodings = ['gbk', 'gb2312', 'big5', 'windows-1252'];
            let success = false;

            for (let encoding of encodings) {
              try {
                const decoder = new TextDecoder(encoding);
                const text = decoder.decode(arrayBuffer);

                // 检查解码后的文本是否合理
                if (this.isReasonableText(text)) {
                  const csvData = Papa.parse(text, {
                    header: true,
                    skipEmptyLines: true
                  });

                  if (csvData.data.length > 0) {
                    this.processCSVData(csvData);
                    this.detectedEncoding = encoding.toUpperCase();
                    this.addLog(`使用 ${encoding.toUpperCase()} 编码解析成功`, 'info');
                    success = true;
                    break;
                  }
                }
              } catch (e) {
                continue; // 尝试下一种编码
              }
            }

            if (!success) {
              this.$message.error('无法解析文件编码，请尝试其他方法');
              this.addLog('所有编码尝试都失败', 'error');
            }
          } catch (err) {
            console.error('二进制解析失败:', err);
            this.$message.error('文件解析失败');
          }
        };

        fileReader.readAsArrayBuffer(this.file);
      },
      // 检查文本是否合理 - 修复正则表达式中的控制字符
      isReasonableText(text) {
        // 检查是否包含过多不可打印字符 - 修复正则表达式
        // 使用Unicode转义序列替代十六进制转义
        // eslint-disable-next-line no-control-regex
        const unprintableCount = (text.match(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g) || []).length;
        const totalChars = text.length;

        if (totalChars > 0 && (unprintableCount / totalChars) > 0.1) {
          return false;
        }

        // 检查是否有合理的文本内容
        const hasReasonableContent = text.length > 0 &&
                (text.includes(',') || text.includes('\n')) && // CSV特征
                text.length < 10000000; // 文件大小合理

        return hasReasonableContent;
      },

      // 处理CSV数据
      processCSVData(csvData) {
        if (csvData.data.length > 0 && csvData.data[0]) {
          this.tableHeader = Object.keys(csvData.data[0]);
          this.tableData = csvData.data.filter(row => {
            // 过滤空行
            return Object.values(row).some(value => value !== undefined && value !== null && value !== '');
          });

          if (this.tableData.length > 0) {
            this.$message.success('CSV文件解析成功！');
            this.addLog(`CSV文件解析成功，共 ${this.tableData.length} 条记录`, 'info');
          } else {
            this.$message.error('CSV文件中没有有效数据');
            this.addLog('CSV文件中没有有效数据', 'error');
          }
        } else {
          this.$message.error('CSV文件中没有数据');
          this.addLog('CSV文件中没有数据', 'error');
        }
      },

      // 转换编码 - 删除未使用的变量
      convertEncoding() {
        if (!this.file || !this.detectedEncoding) {
          this.$message.error('请先检测文件编码');
          return;
        }

        this.converting = true;
        this.addLog(`开始将文件从 ${this.detectedEncoding} 转换为 ${this.targetEncoding}...`, 'info');

        // 模拟转换过程
        setTimeout(() => {
          try {
            // 在实际应用中，这里应该使用iconv-lite等库进行编码转换
            // 这里我们简单地将数据重新导出为CSV
            // 删除未使用的变量 csvContent
            // const csvContent = Papa.unparse(this.tableData); // 这行被删除

            // 创建转换后的数据对象
            this.convertedData = this.tableData;

            this.converting = false;
            this.$message.success('编码转换成功！');
            this.addLog(`编码转换成功，共转换 ${this.tableData.length} 条记录`, 'info');
          } catch (err) {
            this.converting = false;
            this.$message.error('编码转换失败：' + err.message);
            this.addLog('编码转换失败: ' + err.message, 'error');
          }
        }, 1000);
      },

      // 下载转换后的文件
      downloadConvertedFile() {
        if (!this.convertedData) {
          this.$message.error('没有转换后的数据可下载');
          return;
        }

        try {
          // 将数据转换为CSV格式
          const csvContent = Papa.unparse(this.convertedData);

          // 创建Blob对象
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

          // 创建下载链接
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `converted_${this.file.name}`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.$message.success('文件下载成功！');
          this.addLog(`转换后的文件已下载: converted_${this.file.name}`, 'info');
        } catch (err) {
          this.$message.error('文件下载失败：' + err.message);
          this.addLog('文件下载失败: ' + err.message, 'error');
        }
      },

      // 预览转换后的数据 - 修复未使用的action参数
      previewConvertedData() {
        this.$alert(
                `转换成功！共转换 ${this.convertedData.length} 条记录，编码已从 ${this.detectedEncoding} 转换为 ${this.targetEncoding}`,
                '转换结果',
                {
                  confirmButtonText: '确定',
                  callback: () => {
                    // 删除未使用的action参数
                    // 可以在这里添加回调逻辑
                  }
                }
        );
      },

      // 导出原始数据
      exportOriginalData() {
        if (this.tableData.length === 0) {
          this.$message.warning('没有数据可导出');
          return;
        }

        try {
          // 创建工作簿
          const worksheet = XLSX.utils.json_to_sheet(this.tableData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "原始数据");

          // 生成Excel文件并下载
          XLSX.writeFile(workbook, "原始数据.xlsx");
          this.$message.success('数据导出成功！');
          this.addLog('原始数据已导出到Excel文件', 'info');
        } catch (err) {
          this.$message.error('数据导出失败：' + err.message);
          this.addLog('数据导出失败: ' + err.message, 'error');
        }
      },

      // 添加日志
      addLog(message, type = 'info') {
        this.conversionLogs.unshift({
          time: new Date().toLocaleTimeString(),
          message: message,
          type: type
        });

        // 限制日志数量
        if (this.conversionLogs.length > 50) {
          this.conversionLogs = this.conversionLogs.slice(0, 50);
        }
      },

      // 格式化文件大小
      formatFileSize(size) {
        if (size < 1024) {
          return size + ' B';
        } else if (size < 1024 * 1024) {
          return (size / 1024).toFixed(2) + ' KB';
        } else if (size < 1024 * 1024 * 1024) {
          return (size / (1024 * 1024)).toFixed(2) + ' MB';
        } else {
          return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
        }
      },

      // 清空数据
      clearData() {
        this.$confirm('确定要清空所有数据吗?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.tableData = [];
          this.tableHeader = [];
          this.file = null;
          this.detectedEncoding = '';
          this.convertedData = null;
          this.conversionLogs = [];
          this.$message.info('已清空数据');
        }).catch(() => {
          this.$message.info('已取消清空操作');
        });
      }
    }

  };
</script>
<style scoped>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB", Arial, sans-serif;
  }

  body {
    background-color: #f5f7fa;
    color: #606266;
    padding: 20px;
    line-height: 1.6;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .header {
    text-align: center;
    margin-bottom: 30px;
    padding: 20px;
    background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
    border-radius: 8px;
    color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .header h1 {
    font-size: 28px;
    margin-bottom: 10px;
  }

  .header p {
    font-size: 16px;
    opacity: 0.9;
  }

  .main-content {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 20px;
  }

  .upload-section, .preview-section, .conversion-section {
    flex: 1;
    min-width: 300px;
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  .section-title {
    font-size: 18px;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebeef5;
    color: #409EFF;
    display: flex;
    align-items: center;
  }

  .section-title i {
    margin-right: 10px;
  }

  .upload-demo {
    text-align: center;
    margin-bottom: 20px;
  }

  .el-upload-dragger {
    width: 100% !important;
    padding: 20px;
  }

  .upload-tips {
    margin-top: 15px;
    color: #909399;
    font-size: 14px;
    line-height: 1.6;
  }

  .upload-tips p:first-child {
    font-weight: 500;
    margin-bottom: 5px;
  }

  .preview-table {
    margin-top: 20px;
    width: 100%;
  }

  .action-buttons {
    margin-top: 20px;
    display: flex;
    justify-content: center;
    gap: 15px;
    flex-wrap: wrap;
  }

  .footer {
    margin-top: 30px;
    text-align: center;
    color: #909399;
    font-size: 14px;
  }

  .no-data {
    text-align: center;
    padding: 40px 0;
    color: #909399;
  }

  .no-data i {
    font-size: 60px;
    margin-bottom: 15px;
    display: block;
    color: #ebeef5;
  }

  .success-message {
    background-color: #f0f9eb;
    color: #67c23a;
    padding: 15px;
    border-radius: 4px;
    margin-top: 20px;
    display: flex;
    align-items: center;
  }

  .success-message i {
    margin-right: 10px;
    font-size: 18px;
  }

  .file-info {
    margin-top: 15px;
    padding: 10px;
    background-color: #f5f7fa;
    border-radius: 4px;
    font-size: 14px;
  }

  .encoding-options {
    margin-top: 15px;
    padding: 15px;
    background-color: #f5f7fa;
    border-radius: 4px;
  }

  .encoding-options h3 {
    margin-bottom: 10px;
    color: #606266;
  }

  .encoding-tips {
    margin-top: 15px;
    padding: 10px;
    background-color: #e6f7ff;
    border-radius: 4px;
    border-left: 4px solid #1890ff;
  }

  .encoding-tips h4 {
    color: #1890ff;
    margin-bottom: 5px;
  }

  .encoding-tips p {
    font-size: 13px;
    line-height: 1.5;
  }

  .conversion-options {
    margin-top: 20px;
  }

  .conversion-result {
    margin-top: 20px;
    padding: 15px;
    background-color: #f6ffed;
    border-radius: 4px;
    border-left: 4px solid #52c41a;
  }

  .log-area {
    margin-top: 20px;
    max-height: 200px;
    overflow-y: auto;
    padding: 10px;
    background-color: #f5f7fa;
    border-radius: 4px;
    font-family: monospace;
    font-size: 13px;
  }

  .log-entry {
    margin-bottom: 5px;
    padding: 5px;
    border-left: 3px solid #409EFF;
    background-color: white;
  }

  .log-error {
    border-left-color: #f56c6c;
    color: #f56c6c;
  }

  .log-warning {
    border-left-color: #e6a23c;
    color: #e6a23c;
  }

  .file-type-tabs {
    display: flex;
    margin-bottom: 15px;
    border-bottom: 1px solid #ebeef5;
  }

  .file-type-tab {
    padding: 10px 20px;
    cursor: pointer;
    border-bottom: 2px solid transparent;
  }

  .file-type-tab.active {
    color: #409EFF;
    border-bottom: 2px solid #409EFF;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    .main-content {
      flex-direction: column;
    }

    .action-buttons {
      flex-direction: column;
      align-items: center;
    }

    .action-buttons .el-button {
      width: 100%;
      margin-bottom: 10px;
    }
  }

  .step-by-step {
    margin-top: 20px;
    padding: 15px;
    background-color: #fff7e6;
    border-radius: 4px;
    border-left: 4px solid #ffa940;
  }

  .step-by-step h4 {
    color: #ffa940;
    margin-bottom: 10px;
  }

  .step {
    margin-bottom: 10px;
    display: flex;
    align-items: flex-start;
  }

  .step-number {
    background-color: #ffa940;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    flex-shrink: 0;
  }
</style>
